/**
 * Customer Portal (UI) - dual-pane template assignment modal.
 *
 * Supports drag and drop, bulk actions, and inherited assignment visibility.
 */
import { useEffect, useMemo, useState, type DragEvent } from 'react';
import {
  fetchTemplateAssignmentContext,
  fetchTemplates,
  syncTemplateAssignments
} from '../../features/api/client';
import type { ScopeType, TemplateAssignmentContextApiResponse, TemplateItem } from '../../types/portal';

type SortMode = 'key_asc' | 'key_desc' | 'updated_desc';

type DragPayload = {
  templateKey: string;
  source: 'available' | 'assigned';
};

type TemplateAssignmentModalProps = {
  isOpen: boolean;
  scopeType: ScopeType;
  scopeId: string;
  campaignId?: string | null;
  title: string;
  onClose: () => void;
  onSaved?: (nextTemplateKeys: string[]) => void;
};

type TemplateLookupRow = {
  templateKey: string;
  id: string;
  body: string;
  updatedAt: string;
};

function sortRows(rows: TemplateLookupRow[], mode: SortMode): TemplateLookupRow[] {
  const sorted = [...rows];
  if (mode === 'key_desc') {
    sorted.sort((a, b) => b.templateKey.localeCompare(a.templateKey));
    return sorted;
  }
  if (mode === 'updated_desc') {
    sorted.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    return sorted;
  }
  sorted.sort((a, b) => a.templateKey.localeCompare(b.templateKey));
  return sorted;
}

function matchesSearch(row: TemplateLookupRow, searchValue: string): boolean {
  const search = searchValue.trim().toLowerCase();
  if (!search) return true;
  return (
    row.templateKey.toLowerCase().includes(search) ||
    row.id.toLowerCase().includes(search) ||
    row.body.toLowerCase().includes(search)
  );
}

function normalizePhoneScopeId(scopeType: ScopeType, scopeId: string): string {
  if (scopeType !== 'PHONE') {
    return scopeId.trim();
  }
  const digitsOnly = scopeId.replace(/\D/g, '');
  return digitsOnly.length > 0 ? digitsOnly : scopeId.trim();
}

function parseDragPayload(text: string): DragPayload | null {
  try {
    const parsed = JSON.parse(text) as DragPayload;
    if (!parsed?.templateKey || (parsed.source !== 'available' && parsed.source !== 'assigned')) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function summarizeInheritedSources(context: TemplateAssignmentContextApiResponse, templateKey: string): string {
  const item = context.inherited.find((entry) => entry.templateKey === templateKey);
  if (!item) return 'Inherited';
  if (item.from.includes('CAMPAIGN')) return 'Campaign';
  if (item.accountWildcard) return 'Account (all)';
  return 'Account';
}

export function TemplateAssignmentModal({
  isOpen,
  scopeType,
  scopeId,
  campaignId,
  title,
  onClose,
  onSaved
}: TemplateAssignmentModalProps) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [sortMode, setSortMode] = useState<SortMode>('key_asc');
  const [templates, setTemplates] = useState<TemplateItem[]>([]);
  const [contextData, setContextData] = useState<TemplateAssignmentContextApiResponse | null>(null);
  const [assignedKeys, setAssignedKeys] = useState<string[]>([]);
  const [selectedAvailable, setSelectedAvailable] = useState<Set<string>>(new Set());
  const [selectedAssigned, setSelectedAssigned] = useState<Set<string>>(new Set());
  const [dragOverPane, setDragOverPane] = useState<'available' | 'assigned' | null>(null);

  const normalizedScopeId = useMemo(() => normalizePhoneScopeId(scopeType, scopeId), [scopeType, scopeId]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSearchValue('');
    setSortMode('key_asc');
    setSelectedAvailable(new Set());
    setSelectedAssigned(new Set());

    Promise.all([fetchTemplates(), fetchTemplateAssignmentContext(scopeType, normalizedScopeId, campaignId)])
      .then(([templatesResponse, contextResponse]) => {
        if (cancelled) return;
        setTemplates(templatesResponse.items);
        setContextData(contextResponse);
        setAssignedKeys(contextResponse.atScope.map((item) => item.templateKey));
      })
      .catch((requestError) => {
        if (cancelled) return;
        setError(requestError instanceof Error ? requestError.message : String(requestError));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [campaignId, isOpen, normalizedScopeId, scopeType]);

  const templateByKey = useMemo(() => {
    const byKey = new Map<string, TemplateItem>();
    for (const template of templates) {
      byKey.set(template.template_key, template);
    }
    return byKey;
  }, [templates]);

  const inheritedSet = useMemo(() => {
    if (!contextData) return new Set<string>();
    return new Set(contextData.inherited.map((item) => item.templateKey));
  }, [contextData]);

  const assignedSet = useMemo(() => new Set(assignedKeys), [assignedKeys]);

  const availableRows = useMemo(() => {
    const rows = templates
      .map<TemplateLookupRow>((template) => ({
        templateKey: template.template_key,
        id: template.id,
        body: template.body,
        updatedAt: template.updated_at
      }))
      .filter((row) => !inheritedSet.has(row.templateKey) && !assignedSet.has(row.templateKey))
      .filter((row) => matchesSearch(row, searchValue));
    return sortRows(rows, sortMode);
  }, [assignedSet, inheritedSet, searchValue, sortMode, templates]);

  const assignedRows = useMemo(() => {
    const rows = assignedKeys.map<TemplateLookupRow>((templateKey) => {
      const template = templateByKey.get(templateKey);
      if (!template) {
        return {
          templateKey,
          id: templateKey,
          body: '',
          updatedAt: ''
        };
      }
      return {
        templateKey: template.template_key,
        id: template.id,
        body: template.body,
        updatedAt: template.updated_at
      };
    });
    return sortRows(rows.filter((row) => matchesSearch(row, searchValue)), sortMode);
  }, [assignedKeys, searchValue, sortMode, templateByKey]);

  const inheritedRows = useMemo(() => {
    if (!contextData) return [];
    const rows = contextData.inherited.map<TemplateLookupRow>((inheritedRow) => {
      const template = templateByKey.get(inheritedRow.templateKey);
      if (!template) {
        return {
          templateKey: inheritedRow.templateKey,
          id: inheritedRow.templateKey,
          body: '',
          updatedAt: ''
        };
      }
      return {
        templateKey: template.template_key,
        id: template.id,
        body: template.body,
        updatedAt: template.updated_at
      };
    });
    return sortRows(rows.filter((row) => matchesSearch(row, searchValue)), sortMode);
  }, [contextData, searchValue, sortMode, templateByKey]);

  function toggleAvailableSelection(templateKey: string, checked: boolean): void {
    setSelectedAvailable((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(templateKey);
      } else {
        next.delete(templateKey);
      }
      return next;
    });
  }

  function toggleAssignedSelection(templateKey: string, checked: boolean): void {
    setSelectedAssigned((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(templateKey);
      } else {
        next.delete(templateKey);
      }
      return next;
    });
  }

  function addTemplateKeys(keys: Iterable<string>): void {
    const additions = [...new Set([...keys].filter((key) => key && !inheritedSet.has(key)))];
    if (!additions.length) return;
    setAssignedKeys((current) => {
      const nextSet = new Set(current);
      for (const key of additions) nextSet.add(key);
      return [...nextSet];
    });
    setSelectedAvailable(new Set());
  }

  function removeTemplateKeys(keys: Iterable<string>): void {
    const removalSet = new Set([...keys].filter(Boolean));
    if (!removalSet.size) return;
    setAssignedKeys((current) => current.filter((key) => !removalSet.has(key)));
    setSelectedAssigned(new Set());
  }

  function onDragStart(event: DragEvent<HTMLElement>, payload: DragPayload): void {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('application/json', JSON.stringify(payload));
    event.dataTransfer.setData('text/plain', JSON.stringify(payload));
  }

  function onDragOverPane(event: DragEvent<HTMLElement>, pane: 'available' | 'assigned'): void {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (dragOverPane !== pane) {
      setDragOverPane(pane);
    }
  }

  function onDropToAssigned(event: DragEvent<HTMLElement>): void {
    event.preventDefault();
    setDragOverPane(null);
    const payload = parseDragPayload(
      event.dataTransfer.getData('application/json') || event.dataTransfer.getData('text/plain')
    );
    if (!payload || payload.source !== 'available') return;
    addTemplateKeys([payload.templateKey]);
  }

  function onDropToAvailable(event: DragEvent<HTMLElement>): void {
    event.preventDefault();
    setDragOverPane(null);
    const payload = parseDragPayload(
      event.dataTransfer.getData('application/json') || event.dataTransfer.getData('text/plain')
    );
    if (!payload || payload.source !== 'assigned') return;
    removeTemplateKeys([payload.templateKey]);
  }

  async function handleSave(): Promise<void> {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      const response = await syncTemplateAssignments({
        scopeType,
        scopeId: normalizedScopeId,
        templateKeys: assignedKeys
      });
      setAssignedKeys(response.items.map((item) => item.templateKey));
      if (onSaved) onSaved(response.items.map((item) => item.templateKey));
      onClose();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : String(requestError));
    } finally {
      setSaving(false);
    }
  }

  if (!isOpen) {
    return null;
  }

  return (
    <div className="modal modal-blur fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.3)' }} role="dialog" aria-modal="true">
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button type="button" className="btn-close" aria-label="Close" onClick={onClose} disabled={saving} />
          </div>
          <div className="modal-body">
            <div className="row g-2 mb-3">
              <div className="col-md-8">
                <label className="form-label mb-1">Search templates</label>
                <input
                  className="form-control"
                  value={searchValue}
                  onChange={(event) => setSearchValue(event.target.value)}
                  placeholder="Search by template id, key, or body"
                />
              </div>
              <div className="col-md-4">
                <label className="form-label mb-1">Sort</label>
                <select className="form-select" value={sortMode} onChange={(event) => setSortMode(event.target.value as SortMode)}>
                  <option value="key_asc">Template key (A-Z)</option>
                  <option value="key_desc">Template key (Z-A)</option>
                  <option value="updated_desc">Recently updated</option>
                </select>
              </div>
            </div>

            {error ? <div className="alert alert-danger">{error}</div> : null}

            {loading ? (
              <div className="text-secondary">Loading template assignments...</div>
            ) : (
              <div className="row g-3">
                <div className="col-md-6">
                  <div className={`card template-pane ${dragOverPane === 'available' ? 'template-pane--drag-over' : ''}`}>
                    <div className="card-header">
                      <h3 className="card-title mb-0">Assignable templates</h3>
                    </div>
                    <div
                      className="card-body template-pane-body"
                      onDragOver={(event) => onDragOverPane(event, 'available')}
                      onDragLeave={() => setDragOverPane(null)}
                      onDrop={onDropToAvailable}
                    >
                      <div className="btn-list mb-2">
                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => addTemplateKeys(selectedAvailable)}>
                          Add selected
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => addTemplateKeys(availableRows.map((row) => row.templateKey))}
                        >
                          Add filtered
                        </button>
                      </div>

                      {inheritedRows.length ? (
                        <div className="mb-3">
                          <div className="text-secondary small mb-2">
                            Inherited templates are read-only at this scope and cannot be dragged into assigned.
                          </div>
                          <div className="list-group template-list">
                            {inheritedRows.map((row) => (
                              <div key={`inherited-${row.templateKey}`} className="list-group-item template-row template-row--inherited">
                                <div>
                                  <div className="fw-semibold">{row.templateKey}</div>
                                  <div className="text-secondary small">{row.id}</div>
                                </div>
                                <span className="badge bg-secondary-lt text-secondary">
                                  {contextData ? summarizeInheritedSources(contextData, row.templateKey) : 'Inherited'}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null}

                      <div className="list-group template-list">
                        {availableRows.map((row) => (
                          <label
                            key={`available-${row.templateKey}`}
                            className="list-group-item template-row cursor-pointer"
                            draggable
                            onDragStart={(event) => onDragStart(event, { templateKey: row.templateKey, source: 'available' })}
                          >
                            <span className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={selectedAvailable.has(row.templateKey)}
                                onChange={(event) => toggleAvailableSelection(row.templateKey, event.target.checked)}
                              />
                            </span>
                            <span className="template-row-copy">
                              <span className="fw-semibold">{row.templateKey}</span>
                              <span className="text-secondary small">{row.id}</span>
                            </span>
                          </label>
                        ))}
                        {!availableRows.length ? <div className="list-group-item text-secondary small">No assignable templates found.</div> : null}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className={`card template-pane ${dragOverPane === 'assigned' ? 'template-pane--drag-over' : ''}`}>
                    <div className="card-header">
                      <h3 className="card-title mb-0">Assigned at this scope</h3>
                    </div>
                    <div
                      className="card-body template-pane-body"
                      onDragOver={(event) => onDragOverPane(event, 'assigned')}
                      onDragLeave={() => setDragOverPane(null)}
                      onDrop={onDropToAssigned}
                    >
                      <div className="btn-list mb-2">
                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeTemplateKeys(selectedAssigned)}>
                          Remove selected
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => removeTemplateKeys(assignedRows.map((row) => row.templateKey))}
                        >
                          Remove filtered
                        </button>
                      </div>

                      <div className="list-group template-list">
                        {assignedRows.map((row) => (
                          <label
                            key={`assigned-${row.templateKey}`}
                            className="list-group-item template-row cursor-pointer"
                            draggable
                            onDragStart={(event) => onDragStart(event, { templateKey: row.templateKey, source: 'assigned' })}
                          >
                            <span className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={selectedAssigned.has(row.templateKey)}
                                onChange={(event) => toggleAssignedSelection(row.templateKey, event.target.checked)}
                              />
                            </span>
                            <span className="template-row-copy">
                              <span className="fw-semibold">{row.templateKey}</span>
                              <span className="text-secondary small">{row.id}</span>
                            </span>
                          </label>
                        ))}
                        {!assignedRows.length ? <div className="list-group-item text-secondary small">No templates assigned at this scope.</div> : null}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-link link-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary ms-auto" onClick={handleSave} disabled={saving || loading}>
              {saving ? 'Saving...' : 'Save assignments'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
